import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Drawer } from 'vaul';
import { User, LogOut, Globe, Bell, Lock, ChevronRight } from 'lucide-react';
import AppHeader from '../components/AppHeader';
import BottomTabBar from '../components/BottomTabBar';
import LoadingSpinner from '../components/LoadingSpinner';
import { getDriverProfile } from '../api/driversApi';
import NotificationBadge from '../components/NotificationBadge';

function Profile() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [logoutSheetOpen, setLogoutSheetOpen] = useState(false);

  const [driver, setDriver] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    async function fetchProfile() {
      try {
        const result = await getDriverProfile();
        if (result.success) {
          setDriver(result.driver);
        } else {
          setError(result.message || 'Failed to load profile.');
        }
      } catch (err) {
        setError('Could not connect to server.');
      } finally {
        setLoading(false);
      }
    }

    fetchProfile();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const notificationButton = (
    <button style={styles.iconButton}>
      <Bell size={20} color="#374151" />
    </button>
  );

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', paddingBottom: 64, backgroundColor: '#f9fafb' }}>
        <AppHeader title={t('profile.title')} showLogo={true} rightAction={notificationButton} />
        <LoadingSpinner fullScreen />
        <BottomTabBar />
      </div>
    );
  }

  if (error || !driver) {
    return (
      <div style={{ minHeight: '100vh', paddingBottom: 64, backgroundColor: '#f9fafb' }}>
        <AppHeader title={t('profile.title')} showLogo={true} 
    rightAction={<NotificationBadge onClick={() => navigate('/notifications')} />}
		/>
        <div style={{ padding: 16, color: '#dc2626' }}>{error || 'Profile not found.'}</div>
        <BottomTabBar />
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', paddingBottom: 64, backgroundColor: '#f9fafb' }}>
      <AppHeader title={t('profile.title')} showLogo={true} 
	  
	 rightAction={<NotificationBadge onClick={() => navigate('/notifications')} />}
	  />

      <div style={{ padding: 16 }}>
        <div style={styles.avatarSection}>
          <div style={styles.avatarCircle}>
            <User size={32} color="white" />
          </div>
          <div style={styles.driverName}>{driver.driver_name}</div>
        </div>

        <div style={styles.sectionTitle}>{t('profile.personalInfo')}</div>
        <div style={styles.card}>
          <div style={styles.infoRow}>
            <span style={styles.infoLabel}>{t('profile.email')}</span>
            <span style={styles.infoValue}>{driver.email}</span>
          </div>
          <div style={styles.divider} />
          <div style={styles.infoRow}>
            <span style={styles.infoLabel}>{t('profile.phone')}</span>
            <span style={styles.infoValue}>{driver.phone || '—'}</span>
          </div>
          <div style={styles.divider} />
          <div style={styles.linkRow} onClick={() => navigate('/change-password')}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Lock size={16} color="#6b7280" style={{ marginRight: 10 }} />
              <span style={styles.infoLabel}>Change Password</span>
            </div>
            <ChevronRight size={16} color="#9ca3af" />
          </div>
        </div>

        <div style={styles.sectionTitle}>{t('profile.language')}</div>
        <div style={styles.card}>
          <div style={styles.langRow}>
            <div style={styles.langOption}>
              <Globe size={18} color="#6b7280" />
              <span style={{ marginLeft: 10 }}>English</span>
            </div>
            <button
              onClick={() => i18n.changeLanguage('en')}
              style={{
                ...styles.langBtn,
                backgroundColor: i18n.language === 'en' ? '#2563eb' : '#f3f4f6',
                color: i18n.language === 'en' ? 'white' : '#374151',
              }}
            >
              EN
            </button>
          </div>
          <div style={styles.divider} />
          <div style={styles.langRow}>
            <div style={styles.langOption}>
              <Globe size={18} color="#6b7280" />
              <span style={{ marginLeft: 10 }}>Français</span>
            </div>
            <button
              onClick={() => i18n.changeLanguage('fr')}
              style={{
                ...styles.langBtn,
                backgroundColor: i18n.language === 'fr' ? '#2563eb' : '#f3f4f6',
                color: i18n.language === 'fr' ? 'white' : '#374151',
              }}
            >
              FR
            </button>
          </div>
        </div>

        <button onClick={() => setLogoutSheetOpen(true)} style={styles.logoutButton}>
          <LogOut size={18} color="#dc2626" />
          <span style={{ marginLeft: 8 }}>{t('profile.logout')}</span>
        </button>
      </div>

      <BottomTabBar />

      <Drawer.Root open={logoutSheetOpen} onOpenChange={setLogoutSheetOpen}>
        <Drawer.Portal>
          <Drawer.Overlay style={styles.overlay} />
          <Drawer.Content style={styles.sheetContent}>
            <div style={styles.sheetHandle} />
            <div style={styles.sheetTitle}>{t('profile.confirmLogout')}</div>
            <button style={styles.sheetLogoutBtn} onClick={handleLogout}>
              {t('profile.logout')}
            </button>
            <button style={styles.sheetCancel} onClick={() => setLogoutSheetOpen(false)}>
              {t('profile.cancel')}
            </button>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    </div>
  );
}

const styles = {
  iconButton: { background: 'none', border: 'none', cursor: 'pointer', padding: 6 },
  avatarSection: { display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 24 },
  avatarCircle: { width: 72, height: 72, borderRadius: '50%', backgroundColor: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 10 },
  driverName: { fontSize: 17, fontWeight: 700, color: '#111827' },
  sectionTitle: { fontSize: 13, fontWeight: 700, color: '#6b7280', marginBottom: 8, marginTop: 20 },
  card: { backgroundColor: 'white', borderRadius: 12, padding: 4, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  infoRow: { display: 'flex', justifyContent: 'space-between', padding: '12px 14px' },
  infoLabel: { fontSize: 13, color: '#6b7280' },
  infoValue: { fontSize: 13, fontWeight: 600, color: '#111827' },
  linkRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', cursor: 'pointer' },
  divider: { height: 1, backgroundColor: '#f3f4f6', marginLeft: 14 },
  langRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px' },
  langOption: { display: 'flex', alignItems: 'center', fontSize: 14, color: '#111827' },
  langBtn: { border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer' },
  logoutButton: { width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'white', border: '1px solid #fee2e2', borderRadius: 10, padding: '13px 0', marginTop: 28, color: '#dc2626', fontWeight: 600, fontSize: 14, cursor: 'pointer' },
  overlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 60 },
  sheetContent: { position: 'fixed', bottom: 0, left: 0, right: 0, backgroundColor: 'white', borderTopLeftRadius: 20, borderTopRightRadius: 20, padding: '10px 20px 24px', zIndex: 70 },
  sheetHandle: { width: 40, height: 4, backgroundColor: '#d1d5db', borderRadius: 999, margin: '0 auto 16px' },
  sheetTitle: { fontSize: 15, fontWeight: 600, color: '#111827', marginBottom: 16, textAlign: 'center' },
  sheetLogoutBtn: { width: '100%', backgroundColor: '#dc2626', color: 'white', fontWeight: 700, fontSize: 15, padding: '14px 0', borderRadius: 10, border: 'none', marginBottom: 10, cursor: 'pointer' },
  sheetCancel: { width: '100%', backgroundColor: '#f3f4f6', color: '#374151', fontWeight: 600, fontSize: 15, padding: '14px 0', borderRadius: 10, border: 'none', cursor: 'pointer' },
};

export default Profile;